// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import {
  Badge, Box, Tabs, Text,
} from '@usealloy/component-library';

import {
  AccountsOverview,
  DataSources,
  OtherData,
  OutcomeReasons,
  Tags,
} from '../cards';
import { Row, Col } from '../common';
import { useEventTypeEvaluationsContext } from '../context';
import { RecentTransactions, WatchlistMatches } from '../tabs';
import { useGetWatchlistMatches } from '../../watchlist-review/hooks/getWatchlistMatches';
import { UserContext } from '~/dashboard/context';
import { ReviewTabCount } from '../../watchlist-review/styles';
import PartialDetails from '../../PartialDetails/PartialDetails';
import { useRiskData } from '../../RiskRatingCard/useRiskData';
import { RiskRatingCard } from '../../RiskRatingCard/RiskRatingCard';
import { hasDataSourcesData } from '../../utils/cardDataChecks';
import { shouldRenderCard } from '../../utils/shouldRenderCard';

export const TransactionTabs = () => {
  const { evaluation, isInvestigationPage } = useEventTypeEvaluationsContext();
  const {
    evaluation_token: evaluationToken,
    entity_token: entityToken,
  } = evaluation;

  const { typhonDomain, featureFlags, permissions } = useContext(UserContext);
  const hasReadApplicationVersionsPermission = !!permissions?.['READ:ApplicationVersions:null'];
  const hideEmptyCardsEnabled = !!featureFlags?.removeEmptyCards;

  const [{
    data: { matches = [] },
    isLoading: isLoadingWatchlistMatches,
  }] = useGetWatchlistMatches({
    evaluations: [{
      evaluationToken,
      entityToken,
    }],
    typhonDomain,
    showLatestDecisions: true,
  });

  /* ---------- CRA Logic ---------- */
  const {
    riskFactors,
    riskFormula,
    riskRangeValues,
    riskRatingColor,
    riskRatingLabel,
    showRiskTab: shouldRenderRiskTab,
    totalRisk,
  } = useRiskData({
    isRiskFlagEnabled: featureFlags?.customerRiskAssessmentCra,
    riskScoreData: evaluation.final_response.summary?.risk_score,
  });

  /* ---------- vvvvvvvvv ---------- */

  const tabs = useMemo(() => {
    const reviewMatchesLength = matches
      ? matches
        .filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
        .length
      : 0;

    const allTabs: Array<{
      label: React.ReactNode;
      value: string;
      content: React.ReactNode;
    }> = [
      {
        label: 'Overview',
        value: 'overview',
        content: (
          <Row>
            <Col>
              {evaluation.result === 'partial'
                && evaluation.final_response?.warnings
                && <PartialDetails evaluation={evaluation} />}
              <AccountsOverview />
            </Col>
            <Col>
              <OutcomeReasons />
              <Tags />
              {shouldRenderCard(hasDataSourcesData(evaluation), hideEmptyCardsEnabled)
                && <DataSources />}
              <OtherData />
            </Col>
          </Row>
        ),
      },
    ];

    if (!(isInvestigationPage && featureFlags?.investigationBaTrxWebhooks)) {
      allTabs.push({
        label: 'Transactions',
        value: 'transactions',
        content: <RecentTransactions />,
      });
    }

    allTabs.push({
      label: (
        <Box direction="row" gap={1}>
          Watchlist Matches
          {!isLoadingWatchlistMatches
            && reviewMatchesLength > 0 && (
            <ReviewTabCount status="danger">
              <Box justifyContent="center" alignItems="center">
                {reviewMatchesLength}
              </Box>
            </ReviewTabCount>
          )}
        </Box>
      ),
      value: 'watchlist',
      content: <WatchlistMatches evaluation={evaluation} />,
    });

    if (shouldRenderRiskTab) {
      allTabs.push({
        label: (
          <Box>
            <Text style={{ color: 'var(--cl-text-secondary)' }}>Risk Score</Text>
            { riskRatingColor && riskRatingLabel && (
              <Badge outline backgroundColor={riskRatingColor}>{riskRatingLabel}</Badge>
            )}
          </Box>
        ),
        value: 'risk-score',
        content: (
          <RiskRatingCard
            canViewRiskFormula={hasReadApplicationVersionsPermission}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={riskFactors}
            riskOutputFormula={riskFormula}
            riskRanges={riskRangeValues}
            riskValue={totalRisk}
            riskValueColor={riskRatingColor}
            riskValueLabel={riskRatingLabel}
            variant="entityRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }

    return allTabs;
  }, [
    evaluation,
    featureFlags,
    isInvestigationPage,
    isLoadingWatchlistMatches,
    matches,
    shouldRenderRiskTab,
  ]);

  return (
    <Tabs
      style={{ maxWidth: '100%', minWidth: '100%' }}
      tabs={tabs}
    />
  );
};
